import { useCallback, useMemo } from 'react'
import { trpc } from '../../../../lib/trpc.js'
import type {
  Source,
  SourceContent,
  SourceContentItem,
} from '../../sources/types'

export interface SourceWithContent extends Source {
  contents: SourceContent[]
}

/**
 * Hook to fetch prep_resource sources with their content (chapters, modules, etc.)
 * Used by the SourceContentWidget to display assignable reading tasks
 */
export function useSourcesWithContent() {
  const utils = trpc.useUtils()

  // Fetch all sources
  const {
    data: sourcesData,
    isLoading: sourcesLoading,
    error: sourcesError,
  } = trpc.admin.sources.list.useQuery()

  // Fetch all source content (unfiltered — we filter client-side)
  const {
    data: contentData,
    isLoading: contentLoading,
    error: contentError,
  } = trpc.admin.content.listSourceContent.useQuery()

  const loading = sourcesLoading || contentLoading
  const error = sourcesError?.message ?? contentError?.message ?? null

  // Combine sources with their content, applying the same filters and sorting
  // as the original fetch-based implementation
  const sourcesWithContent = useMemo<SourceWithContent[]>(() => {
    if (!sourcesData || !contentData) return []

    const allSources = sourcesData as Source[]
    const allContent = (contentData.data ?? []) as SourceContent[]

    // Filter to only prep_resource category sources
    const prepResourceSources = allSources.filter(
      (s) => s.category === 'prep_resource',
    )

    // Group content by sourceId, filtering to root-level (no parentId) and sorting by order
    const contentBySourceId = new Map<string, SourceContent[]>()
    for (const c of allContent) {
      if (c.parentId) continue // Only root-level content
      const existing = contentBySourceId.get(c.sourceId) ?? []
      existing.push(c)
      contentBySourceId.set(c.sourceId, existing)
    }

    // Sort each source's content by order
    for (const contents of contentBySourceId.values()) {
      contents.sort((a, b) => a.order - b.order)
    }

    // Build SourceWithContent array
    const result = prepResourceSources.map((source) => ({
      ...source,
      contents: contentBySourceId.get(source.id) ?? [],
    }))

    // Filter out sources with no content and sort:
    // 1. BCTCI first (by checking if name contains "Beyond" or slug is "bctci")
    // 2. Then alphabetically by name
    return result
      .filter((s) => s.contents.length > 0)
      .sort((a, b) => {
        const aIsBctci =
          a.name.toLowerCase().includes('beyond') || a.slug === 'bctci'
        const bIsBctci =
          b.name.toLowerCase().includes('beyond') || b.slug === 'bctci'

        if (aIsBctci && !bIsBctci) return -1
        if (!aIsBctci && bIsBctci) return 1
        return a.name.localeCompare(b.name)
      })
  }, [sourcesData, contentData])

  /**
   * Fetch items for a specific chapter/sourceContent
   * Returns the items array with populated content data
   */
  const fetchChapterItems = useCallback(
    async (sourceContentId: string): Promise<SourceContentItem[]> => {
      const result = await utils.admin.content.listSourceContentItems.fetch({
        sourceContentId,
      })
      return (result.data ?? []) as SourceContentItem[]
    },
    [utils],
  )

  const refresh = useCallback(async () => {
    await Promise.all([
      utils.admin.sources.list.invalidate(),
      utils.admin.content.listSourceContent.invalidate(),
    ])
  }, [utils])

  return {
    sourcesWithContent,
    loading,
    error,
    refresh,
    fetchChapterItems,
  }
}
